'use client';
import dynamic from 'next/dynamic';
import { ReactNode, forwardRef, Ref, ComponentProps } from 'react';
import { useUserDeviceType, DeviceTypes } from '@/hooks';
import cn from 'classnames';
const Button = dynamic(() => import('de-sxm-reactlib-2/components/atoms/Button/Button'));
import styles from './stickycta.module.scss';
import ImageWrapper from '@/shared-components/ImageWrapper/ImageWrapper';
import React from 'react';

const {
    mainWrapperStyles,
    imagesWrapperStyles,
    staticImageStyles,
    qrCodeStyles,
    bodyTextStyles,
    ctaButtonStyles,
    hideOnMobile,
    displayButton,
} = styles;

type ImagePropTypes = {
    url: string;
    altText: string;
    width?: number;
    height?: number;
};

type ButtonTheme = ComponentProps<typeof Button>['variant'];

interface StickyCtaProps {
    staticImage?: ImagePropTypes;
    qrCode?: ImagePropTypes;
    bodyText?: ReactNode;
    ctaText?: React.JSX.Element;
    ctaUrl?: string;
    headerFooterBarVersion?: boolean;
    variant?: ButtonTheme;
    buttonType?: string;
}

const StickyCta = forwardRef(
    (
        {
            staticImage,
            qrCode,
            bodyText,
            ctaText,
            ctaUrl,
            headerFooterBarVersion = false,
            variant = 'primary',
            buttonType,
        }: StickyCtaProps,
        ref: Ref<HTMLDivElement>,
    ): React.JSX.Element => {
        const userDeviceType = useUserDeviceType();
        const isMobileDevice = userDeviceType === DeviceTypes.android || userDeviceType === DeviceTypes.ios;

        const mainWrapper = cn(mainWrapperStyles, {
            [hideOnMobile]: isMobileDevice,
        });

        return (
            <>
                <div className={mainWrapper} ref={ref}>
                    <div className={imagesWrapperStyles}>
                        {staticImage?.url && (
                            <ImageWrapper
                                src={staticImage.url}
                                alt={staticImage.altText}
                                optimizedHeight={staticImage.height}
                                optimizedWidth={staticImage.width}
                                className={staticImageStyles}
                            />
                        )}
                        {qrCode?.url && (
                            <ImageWrapper
                                src={qrCode.url}
                                alt={qrCode.altText}
                                className={qrCodeStyles}
                                optimizedHeight={qrCode.height}
                                optimizedWidth={qrCode.width}
                            />
                        )}
                    </div>
                    {!headerFooterBarVersion && <div className={bodyTextStyles}>{bodyText}</div>}
                </div>
                {headerFooterBarVersion && (
                    <div>
                        <Button
                            variant={variant}
                            type="button"
                            href={ctaUrl}
                            className={isMobileDevice ? displayButton : ctaButtonStyles}
                            dataCy="atoms-sticky-cta"
                            buttonType={buttonType}
                            tertiaryNoPadding={variant === 'tertiary'}
                            colorSet='base'
                        >
                            {ctaText}
                        </Button>
                    </div>
                )}
            </>
        );
    },
);

StickyCta.displayName = 'StickyCta';
export default StickyCta;
